import React, { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TextField,
  InputAdornment,
  MenuItem,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  Button,
} from '@mui/material'
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  // Assessment as ReportIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import {
  smbsApi,
  SMBData,
  SMBDetailData,
  WebsiteScrapeMetadata,
  WebsiteScrapeResponse,
} from '../../services/api'
import { useNotification } from '../../contexts/NotificationContext'
import { useAuth } from '../../contexts/AuthContext'
import AddSMBDialog from '../dialogs/AddSMBDialog'
import EditSMBDialog from '../dialogs/EditSMBDialog'
import ConfirmDialog from '../dialogs/ConfirmDialog'
import { SMBDetailsDrawer } from './SMBDetailsDrawer'
import { MetaEmbeddedSignupButton } from '../../features/MetaEmbeddedSignup'

interface SMBListProps {
  maxHeight?: number
}

const SMBList: React.FC<SMBListProps> = ({ maxHeight = 600 }) => {
  const [smbs, setSMBs] = useState<SMBData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedSMB, setSelectedSMB] = useState<SMBData | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [detailsSMB, setDetailsSMB] = useState<SMBDetailData | null>(null)
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false)
  const [detailsLoading, setDetailsLoading] = useState(false)
  const [scrapeLoading, setScrapeLoading] = useState<Record<string, boolean>>({})
  const [scrapeInfo, setScrapeInfo] = useState<Record<string, WebsiteScrapeMetadata>>({})
  const [scrapingInProgress, setScrapingInProgress] = useState<Set<string>>(new Set())
  const { showNotification } = useNotification()
  const { user } = useAuth()

  const statusColors = {
    active: 'success',
    inactive: 'default',
    trial: 'warning',
  } as const

  const fetchSMBs = async () => {
    try {
      setLoading(true)
      const params = {
        search: searchQuery || undefined,
        status: statusFilter || undefined,
        page: 1,
        limit: 50,
      }
      const response = await smbsApi.getSMBs({
        search: params.search,
        status: params.status,
        page: params.page,
        per_page: params.limit,
      })
      setSMBs(response.smbs || [])
      setError(null)
    } catch (err: any) {
      console.error('Error fetching SMBs:', err)
      setError(err.response?.data?.detail || 'Failed to load businesses')
      setSMBs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchSMBs()
  }, [searchQuery, statusFilter])

  // Handle scraping promise resolution
  const handleScrapingPromise = (smbId: string, promise: Promise<any>) => {
    setScrapingInProgress(prev => new Set(prev).add(smbId))

    promise
      .then((response) => {
        const updatedSMB = response.smb || response
        showNotification('SMB updated successfully!', 'success')
        
        setSMBs(prev => prev.map(s => s.id === smbId ? { ...s, ...updatedSMB } : s))
        setDetailsSMB(currentDetails => {
          if (currentDetails?.id === smbId) {
            return { ...currentDetails, ...updatedSMB }
          }
          return currentDetails
        })

        if (updatedSMB.scrape_metadata) {
          setScrapeInfo(prev => ({ ...prev, [smbId]: updatedSMB.scrape_metadata }))
        }
      })
      .catch((err) => {
        console.error('Update failed:', err)
        const errorMessage = err.response?.data?.error?.message?.message|| 'An unexpected error occurred during the update.'
        showNotification(errorMessage, 'error')
        fetchSMBs()
      })
      .finally(() => {
        setScrapingInProgress(prev => {
          const next = new Set(prev)
          next.delete(smbId)
          return next
        })
      })
  }

  const handleAddSuccess = async (newSMB: SMBData, scrapingPromise?: Promise<any>) => {
    setSMBs(prev => [newSMB, ...prev])
    showNotification(`Business "${newSMB.name}" added successfully!`, 'success')
    if (newSMB.id && scrapingPromise) {
      handleScrapingPromise(newSMB.id, scrapingPromise)
    }
  }

  const handleMetaSignupCompleted = async () => {
    await fetchSMBs()
  }

  const handleEditSuccess = (optimisticSMB: SMBData, updatePromise?: Promise<any>) => {
    if (optimisticSMB.id && updatePromise) {
      setScrapingInProgress(prev => new Set(prev).add(optimisticSMB.id!))
      handleScrapingPromise(optimisticSMB.id, updatePromise)
    } else {
      setSMBs(prev => prev.map(smb => (smb.id === optimisticSMB.id ? optimisticSMB : smb)))
    }
  }

  const handleEditClick = (smb: SMBData) => {
    setSelectedSMB(smb)
    setEditDialogOpen(true)
  }

  const openDetailsForSMB = async (smbId: string) => {
    setDetailsDrawerOpen(true)
    setDetailsLoading(true)
    try {
      const detail = await smbsApi.getSMB(smbId)
      setDetailsSMB(detail)
      setScrapeInfo(prev => {
        const next = { ...prev }
        if (detail.scrape_metadata) {
          next[smbId] = detail.scrape_metadata
        } else {
          delete next[smbId]
        }
        return next
      })
    } catch (error: any) {
      console.error('Error loading SMB details:', error)
      showNotification(error.response?.data?.detail || 'Failed to load business details', 'error')
      setDetailsSMB(null)
    } finally {
      setDetailsLoading(false)
    }
  }

  const isCooldownActive = (smbId?: string) => {
    if (!smbId) return false
    const info = scrapeInfo[smbId]
    if (!info?.cooldown_expires_at) return false
    return new Date(info.cooldown_expires_at).getTime() > Date.now()
  }

  const handleRefreshWebsite = async (smb: SMBData | SMBDetailData) => {
    const smbId = smb.id
    if (!smbId) return
    if (!smb.website) {
      showNotification('This business does not have a website configured.', 'warning')
      return
    }

    if (scrapeLoading[smbId]) {
      return
    }

    setScrapeLoading(prev => ({ ...prev, [smbId]: true }))

    try {
      const response: WebsiteScrapeResponse = await smbsApi.createUpdateSMB({smb_id: smbId, refresh_scrape: true})
      setScrapeInfo(prev => ({
        ...prev,
        [smbId]: {
          cooldown_expires_at: response.cooldown_expires_at ?? null,
          last_run_at: response.last_run_at ?? null,
          last_status: response.last_status ?? null,
        },
      }))

      const lastRunMessage = response.last_run_at
        ? `Last run ${new Date(response.last_run_at).toLocaleString()} (${response.last_status || 'unknown'})`
        : undefined

      showNotification(
        [response.message || 'Website scraped successfully! ', lastRunMessage].filter(Boolean).join(' — '),
        'success'
      )

      if (detailsDrawerOpen && detailsSMB?.id === smbId) {
        await openDetailsForSMB(smbId)
      }
    } catch (error: any) {
      const detail = error.response?.data?.error?.message

      if (error.response?.status === 429 && detail) {
        const retryMessage = detail.message || 'Website scrape recently triggered. Try again later.'
        showNotification(retryMessage, 'info')
      } else if (typeof detail === 'string') {
        showNotification(detail, 'error')
      } else if (detail?.message) {
        showNotification(detail.message, 'error')
      } else {
        showNotification('Failed to trigger website scrape.', 'error')
      }
    } finally {
      setScrapeLoading(prev => ({ ...prev, [smbId]: false }))
    }
  }

  const handleViewClick = (smb: SMBData) => {
    if (smb.id) {
      void openDetailsForSMB(smb.id)
    }
  }

  // const handleReportClick = (smb: SMBData) => {
  //   showNotification(`Generating report for ${smb.name}...`, 'info')
  //   // TODO: Generate report functionality
  // }

  const handleDeleteClick = (smb: SMBData) => {
    setSelectedSMB(smb)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedSMB) return
    try {
      await smbsApi.deleteSMB(String(selectedSMB.id))
      setSMBs(prev => prev.filter(smb => smb.id !== selectedSMB.id))
      showNotification(`Business "${selectedSMB.name}" deleted`, 'success')
      if (detailsSMB?.id === selectedSMB.id) {
        setDetailsDrawerOpen(false)
        setDetailsSMB(null)
      }
    } catch (error: any) {
      console.error('Error deleting SMB:', error)
      showNotification(error.response?.data?.detail || 'Failed to delete business', 'error')
    } finally {
      setDeleteDialogOpen(false)
      setSelectedSMB(null)
    }
  }
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const getStatusChip = (status: string) => {
    const color = statusColors[status as keyof typeof statusColors] || 'default'
    return (
      <Chip 
        label={status.charAt(0).toUpperCase() + status.slice(1)}
        color={color}
        size="small"
        variant="outlined"
      />
    )
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase()
  }

  if (loading && smbs.length === 0) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', sm: 'flex-start' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 3,
          }}>
            <Box>
              <Typography variant="h6" component="h3" fontWeight="bold">
                Client Businesses
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {smbs.length} businesses total
              </Typography>
            </Box>
            <Box sx={{
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'flex-start' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 1,
            }}>
              {user?.role === 'agency' && user.id && (
                <MetaEmbeddedSignupButton
                  onCompleted={handleMetaSignupCompleted}
                  sx={{ width: { xs: '100%', md: 'auto' } }}
                  buttonSx={{ width: { xs: '100%', md: 'auto' } }}
                />
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddDialogOpen(true)}
                sx={{ minWidth: 140, alignSelf: { xs: 'stretch', md: 'flex-start' } }}
              >
                Add Business
              </Button>
            </Box>
          </Box>

          {/* Search and Filter */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            <TextField
              placeholder="Search businesses..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
            <TextField
              select
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              sx={{ minWidth: 150 }}
              SelectProps={{ displayEmpty: true }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="trial">Trial</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
              <Button onClick={fetchSMBs} size="small" sx={{ ml: 2 }}>
                Retry
              </Button>
            </Alert>
          )}

          {/* Table */}
          <TableContainer 
            component={Paper} 
            variant="outlined"
            sx={{ maxHeight }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Business</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Leads (30d)</TableCell>
                  <TableCell align="right">Conversion</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {smbs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography variant="body1" color="text.secondary">
                          {searchQuery || statusFilter ? 'No businesses match your filters' : 'No businesses added yet'}
                        </Typography>
                        {!searchQuery && !statusFilter && (
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setAddDialogOpen(true)}
                          >
                            Add Your First Business
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  smbs.map((smb) => {
                    const info = smb.id ? scrapeInfo[smb.id] : undefined
                    const details: string[] = []
                    if (info?.last_run_at) {
                      details.push(
                        `Last run: ${new Date(info.last_run_at).toLocaleString()} (${info.last_status || 'unknown'})`
                      )
                    }
                    if (isCooldownActive(smb.id)) {
                      details.push('Cooldown active')
                    }
                    const tooltipText = details.length > 0 ? details.join(' • ') : 'Refresh website content'
                    const cooldownActive = isCooldownActive(smb.id)
                    const isScraping = smb.id ? (
                      scrapeLoading[smb.id] || 
                      scrapeInfo[smb.id]?.last_status === 'queued' || 
                      scrapeInfo[smb.id]?.last_status === 'processing'
                    ) : false
                    const isScrapingNow = smb.id ? scrapingInProgress.has(smb.id) : false

                    return (
                      <TableRow key={smb.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main' }}>
                            {getInitials(smb.name)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {smb.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {smb.business_type.charAt(0).toUpperCase() + smb.business_type.slice(1)}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(smb.status || 'active')}
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {smb.metrics?.total_leads || 0}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {smb.metrics?.conversion_rate || 0}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {formatCurrency(smb.metrics?.revenue_30d || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isScrapingNow ? (
                          <Chip 
                            label="Processing" 
                            size="small" 
                            color="info" 
                            icon={<CircularProgress size={16} />}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title={tooltipText}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="info"
                                  disabled={cooldownActive || !smb.website || isScraping}
                                  onClick={() => handleRefreshWebsite(smb)}
                                >
                                  {isScraping ? (
                                    <CircularProgress size={16} thickness={6} />
                                  ) : (
                                    <RefreshIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="View Dashboard">
                              <IconButton
                                size="small"
                                onClick={() => handleViewClick(smb)}
                                color="primary"
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Business">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(smb)}
                                color="default"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* <Tooltip title="Generate Report">  Uncomment it whenever this feature is ready
                              <IconButton
                                size="small"
                                onClick={() => handleReportClick(smb)}
                                color="success"
                              >
                                <ReportIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}
                            <Tooltip title="Delete Business">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(smb)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && smbs.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AddSMBDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={handleAddSuccess}
      />

      {selectedSMB && (
        <EditSMBDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false)
            setSelectedSMB(null)
          }}
          smb={selectedSMB}
          onSuccess={handleEditSuccess}
        />
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Business"
        description={`Are you sure you want to delete ${selectedSMB?.name}?`}
        confirmLabel="Delete"
        onClose={() => {
          setDeleteDialogOpen(false)
          setSelectedSMB(null)
        }}
        onConfirm={confirmDelete}
      />

      <SMBDetailsDrawer
        open={detailsDrawerOpen}
        smb={detailsSMB}
        onClose={() => {
          setDetailsDrawerOpen(false)
          setDetailsSMB(null)
        }}
        onRefreshWebsite={(detail) => handleRefreshWebsite(detail)}
        refreshing={detailsSMB?.id ? Boolean(scrapeLoading[detailsSMB.id]) : false}
        scrapeMeta={detailsSMB?.id ? scrapeInfo[detailsSMB.id] : undefined}
        loading={detailsLoading}
        onReload={(smbId) => openDetailsForSMB(smbId)}
      />
    </>
  )
}

export default SMBList
